/**
 * GatesQueueScreen — Fila de Quality Gates pendentes (F2-FE-2).
 *
 * Porta a `GatesScreen` do protótipo (rota `gates`), lendo:
 *   GET /gates → { total, items:[{ gate_id, execution_id, status, sla_tier, approvers[], created_at }] }
 *
 * Features:
 *   - Lista de gates com execução, SLA, aprovadores e status
 *   - Botão "Revisar" → GateAScreen com ?execution=<id>
 *   - Polling a cada 30s (no hook) + botão de atualizar manual
 *   - Paginação simples
 *   - Tratamento de 403 com ForbiddenBanner
 *
 * Nunca exibe wallet_txn_id.
 *
 * Acessibilidade:
 *   - Tabela com caption + scope
 *   - aria-live="polite" na contagem
 *   - Foco visível nos botões
 *
 * Standards: Clean, NFR (a11y), OWASP.
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck, RefreshCw, ChevronLeft, ChevronRight,
  Clock, Users, AlertCircle, CheckCircle, HelpCircle, XCircle,
} from 'lucide-react';
import { C } from '@/theme/palette';
import { Card, Btn, Badge, ForbiddenBanner } from '@/components/ui';
import { LoadingSpinner, QueryError } from '@/components/ui/QueryState';
import { useGatesQueue } from '@/api/executions';

// ─── Constantes ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;

const STATUS_META = {
  pending:  { label: 'PENDENTE',               color: C.digital, Icon: Clock       },
  approved: { label: 'APROVADO',               color: C.cyber,   Icon: CheckCircle },
  rejected: { label: 'REJEITADO',              color: C.hot,     Icon: XCircle     },
  clarify:  { label: 'ESCLARECIMENTO',         color: C.neon,    Icon: HelpCircle  },
};

const SLA_LABEL = {
  standard:  '24h',
  expedited: '12h',
  critical:  '4h',
};

// ─── Sub-componentes ──────────────────────────────────────────────────────────

const TH_STYLE = {
  textAlign: 'left',
  padding: '10px 16px',
  fontSize: 11,
  fontWeight: 600,
  color: C.neutralMuted,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  borderBottom: `1px solid ${C.blackBorder}`,
  whiteSpace: 'nowrap',
};

const TD_STYLE = {
  padding: '14px 16px',
  fontSize: 13,
  verticalAlign: 'middle',
  borderBottom: `1px solid ${C.blackBorder}`,
};

/** Linha da tabela de gates */
function GateRow({ item, onReview }) {
  const meta = STATUS_META[item.status] ?? STATUS_META.pending;
  const slaLabel = SLA_LABEL[item.sla_tier] ?? item.sla_tier ?? '24h';
  const isPending = item.status === 'pending';

  const createdAt = item.created_at
    ? new Date(item.created_at).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '—';

  return (
    <tr
      style={{ transition: 'background-color 100ms' }}
      className="hover:bg-[#161616]"
    >
      {/* ID do gate (curto) */}
      <td style={{ ...TD_STYLE, fontFamily: 'monospace', fontSize: 11, color: C.neutralMuted }}>
        {item.gate_id?.slice(0, 8) ?? '—'}
      </td>

      {/* Tipo do gate (A ou B) */}
      <td style={TD_STYLE}>
        {item.gate_type && (
          <span style={{
            fontSize:        10,
            fontWeight:      700,
            padding:         '2px 6px',
            borderRadius:    4,
            backgroundColor: item.gate_type === 'b' ? `${C.quantum}22` : `${C.cyber}22`,
            border:          `1px solid ${item.gate_type === 'b' ? C.quantum : C.cyber}44`,
            color:           item.gate_type === 'b' ? C.quantum : C.cyber,
            textTransform:   'uppercase',
            letterSpacing:   '0.04em',
          }}>
            Gate {item.gate_type.toUpperCase()}
          </span>
        )}
      </td>

      {/* Execução */}
      <td style={{ ...TD_STYLE, fontFamily: 'monospace', fontSize: 11, color: C.neutralDim }}>
        {item.execution_id?.slice(0, 12) ?? '—'}
      </td>

      {/* Status */}
      <td style={TD_STYLE}>
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 10,
          fontWeight: 700,
          padding: '3px 8px',
          borderRadius: 4,
          backgroundColor: `${meta.color}22`,
          border: `1px solid ${meta.color}44`,
          color: meta.color,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}>
          <meta.Icon size={10} aria-hidden="true" />
          {meta.label}
        </span>
      </td>

      {/* SLA */}
      <td style={{ ...TD_STYLE, color: C.neutralDim }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <Clock size={11} aria-hidden="true" style={{ color: C.neutralMuted }} />
          {slaLabel}
        </span>
      </td>

      {/* Aprovadores */}
      <td style={{ ...TD_STYLE, color: C.neutralDim }}>
        {item.approvers && item.approvers.length > 0
          ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <Users size={11} aria-hidden="true" style={{ color: C.neutralMuted, flexShrink: 0 }} />
              <span style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                {item.approvers.join(', ')}
              </span>
            </span>
          )
          : <span style={{ color: C.neutralMuted }}>—</span>}
      </td>

      {/* Criado em */}
      <td style={{ ...TD_STYLE, color: C.neutralMuted, fontSize: 12 }}>
        {createdAt}
      </td>

      {/* Ação */}
      <td style={{ ...TD_STYLE, textAlign: 'right' }}>
        <Btn
          variant={isPending ? 'outline' : 'ghost'}
          size="sm"
          onClick={() => onReview(item)}
          aria-label={`Revisar gate ${item.gate_id?.slice(0, 8)} da execução ${item.execution_id?.slice(0, 12)}`}
        >
          {isPending ? 'Revisar' : 'Ver detalhes'}
        </Btn>
      </td>
    </tr>
  );
}

// ─── Tela principal ───────────────────────────────────────────────────────────

/**
 * GatesQueueScreen — fila de gates pendentes do tenant.
 */
export function GatesQueueScreen() {
  // useNavigate direto para preservar query params na navegação para o gate
  const routerNavigate = useNavigate();
  const [page, setPage] = useState(0);

  const filters = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };

  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useGatesQueue(filters);

  const handleReview = useCallback((item) => {
    // Roteia Gate A vs Gate B com base em gate_type (Onda C)
    // Fallback: se gate_type não está presente, usa Gate A (comportamento anterior)
    const gateType = item.gate_type ?? 'a';
    const path = gateType === 'b'
      ? `/gates/b?execution=${item.execution_id}`
      : `/gates/negocio?execution=${item.execution_id}`;
    routerNavigate(path);
  }, [routerNavigate]);

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 20px' }}>

      {/* ── Header da tela ────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 24,
        flexWrap: 'wrap',
        gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ShieldCheck size={20} aria-hidden="true" style={{ color: C.digital }} />
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: C.neutral, margin: 0 }}>
              Fila de Quality Gates
            </h1>
            <p
              aria-live="polite"
              aria-atomic="true"
              style={{ fontSize: 12, color: C.neutralMuted, margin: '2px 0 0' }}
            >
              {data != null
                ? `${data.total} gate${data.total !== 1 ? 's' : ''} no tenant`
                : 'Carregando...'}
            </p>
          </div>
        </div>

        <Btn
          variant="ghost"
          size="sm"
          icon={RefreshCw}
          disabled={isFetching}
          onClick={() => refetch()}
          aria-label="Atualizar fila de gates"
        >
          {isFetching ? 'Atualizando...' : 'Atualizar'}
        </Btn>
      </div>

      {/* ── Estados de loading/erro ──────────────────────────────────────── */}
      {isLoading && <LoadingSpinner label="Carregando fila de gates..." />}

      {error && (
        error.isForbidden
          ? <ForbiddenBanner detail={error.detail} />
          : <QueryError error={error} onRetry={refetch} />
      )}

      {/* ── Tabela ──────────────────────────────────────────────────────── */}
      {!isLoading && !error && data && (
        <>
          {data.items.length === 0
            ? (
              <Card>
                <div style={{
                  padding: 48,
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 12,
                }}>
                  <CheckCircle size={36} aria-hidden="true" style={{ color: C.cyber }} />
                  <div style={{ fontSize: 15, fontWeight: 600, color: C.neutralDim }}>
                    Nenhum gate pendente
                  </div>
                  <div style={{ fontSize: 13, color: C.neutralMuted }}>
                    Todos os Quality Gates foram processados ou ainda não foram criados.
                  </div>
                </div>
              </Card>
            )
            : (
              <Card padded={false} style={{ overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table
                    style={{ width: '100%', borderCollapse: 'collapse' }}
                    aria-label="Fila de Quality Gates"
                  >
                    <caption style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>
                      Lista de Quality Gates pendentes do tenant
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col" style={TH_STYLE}>Gate ID</th>
                        <th scope="col" style={TH_STYLE}>Tipo</th>
                        <th scope="col" style={TH_STYLE}>Execução</th>
                        <th scope="col" style={TH_STYLE}>Status</th>
                        <th scope="col" style={TH_STYLE}>SLA</th>
                        <th scope="col" style={TH_STYLE}>Aprovadores</th>
                        <th scope="col" style={TH_STYLE}>Criado em</th>
                        <th scope="col" style={{ ...TH_STYLE, textAlign: 'right' }}>Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((item) => (
                        <GateRow
                          key={item.gate_id}
                          item={item}
                          onReview={handleReview}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Paginação */}
                {totalPages > 1 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    borderTop: `1px solid ${C.blackBorder}`,
                  }}>
                    <span style={{ fontSize: 12, color: C.neutralMuted }}>
                      Página {page + 1} de {totalPages} · {data.total} gates
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Btn
                        variant="ghost"
                        size="sm"
                        icon={ChevronLeft}
                        disabled={page === 0}
                        onClick={() => setPage((p) => p - 1)}
                        aria-label="Página anterior"
                      >
                        Anterior
                      </Btn>
                      <Btn
                        variant="ghost"
                        size="sm"
                        disabled={page >= totalPages - 1}
                        onClick={() => setPage((p) => p + 1)}
                        aria-label="Próxima página"
                      >
                        Próxima
                        <ChevronRight size={14} aria-hidden="true" />
                      </Btn>
                    </div>
                  </div>
                )}
              </Card>
            )}
        </>
      )}
    </div>
  );
}
